import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import '@/lib/chartjs-register';
import { convertChartOptions } from '@/lib/chartjs-register';
import { ChartWidget, useChartColors } from '@/components/ui/shared/ChartWidget';
import { userService } from '../_services/user.service';
import ColorJson from '../../src/whitelabel.json';
import { InfoHint } from '@/components/ui/info-hint';

/**
 * ColumnPieChart - Functional component for Pie/List toggle widget
 * 
 * Replaces the class-based Column_Piechart component with modern React patterns
 */
function ColumnPieChart({ data, handlechangea, _Index, hideLegend = false }) {
    // State - only show loader if data is ready to fetch
    const [pieChartData, setPieChartData] = useState(null);
    const [listData, setListData] = useState([]);
    const [listHeaders, setListHeaders] = useState([]);
    const [legendValue, setLegendValue] = useState('');
    const [legendText, setLegendText] = useState('');
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState('');
    const [exportLoading, setExportLoading] = useState(false);

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Get user data
    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    // Determine if RxHacker theme
    const isRxHacker = useMemo(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        return host === "rxhacker.com" || 
               host === "nulifespanrx.com" ||
               host === "localhost:5464" || 
               window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
               window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
    }, []);

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return [];
            }
        });
    }, []);

    // Transform colors for RxHacker theme
    const transformColors = useCallback((colors) => {
        if (!isRxHacker || !colors) return colors;
        
        return colors.map(color => {
            let transformed = color;
            transformed = transformed.replace('#2A524B', ColorJson.ApplicationColor.Rxhacker_secondary_color);
            transformed = transformed.replace('#FF8B6C', ColorJson.ApplicationColor.Rxhacker_extra_color);
            transformed = transformed.replace('#44B672', ColorJson.ApplicationColor.Rxhacker_primary_color);
            transformed = transformed.replace('#3CB26D', ColorJson.ApplicationColor.Rxhacker_primary_color);
            transformed = transformed.replace('#A6DBBD', ColorJson.ApplicationColor.Rxhacker_fourth_color);
            return transformed;
        });
    }, [isRxHacker]);

    // Export widget data as CSV
    const handleExport = useCallback(() => {
        try {
            const widgetTitle = data.title || 'export';
            const tableData = [];
            
            // Build CSV from list data
            if (listHeaders.length > 0) {
                tableData.push(listHeaders.join(','));
            }
            
            listData.forEach(row => {
                const rowData = row.map((cell, cellIndex) => {
                    let cellValue = String(cell || '').split(',').join('');
                    
                    // Check if this looks like a date in format XX/XX or XX/XX/XXXX
                    // Add a leading space to prevent Excel from auto-formatting dates
                    if (cellIndex === 0 && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(cellValue)) {
                        cellValue = ` ${cellValue}`;
                    }
                    
                    return cellValue;
                });
                tableData.push(rowData.join(','));
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${widgetTitle}.csv`;
            link.href = url;
            document.body.appendChild(link);
            
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (err) {

        }
    }, [data.title, listData, listHeaders]);

    // Handle list item click (for filtering)
    const handleListItemClick = useCallback((row, rowIdx, cellIdx = 0) => {
        // Disable clicking for widgets that should not add filters
        const noFilterTitles = ['Active/Inactive Brokers by Transactions', 'Active/Inactive Brokers by Enrollments'];
        if (data.DataType === 'NA' || noFilterTitles.some(t => data.title?.includes(t))) {
            return;
        }
        
        if (handlechangea && listHeaders.length > 0) {
            // Use DataType if available, otherwise use the first column header
            const heading = data.DataType && data.DataType !== 'NA' 
                ? data.DataType 
                : listHeaders[0];
            
            let filterValue = row[0];
            
            // Strip state code from pharmacy city names
            // Handles formats: "CITY, ST" or "CITY ST"
            if (heading === 'PhCity') {
                filterValue = String(filterValue)
                    .trim()
                    .replace(/,\s*[A-Z]{2}$/, '')  // Remove ", ST" format
                    .replace(/\s+[A-Z]{2}$/, '')   // Remove " ST" format
                    .trim();
            }
            
            const filterData = {
                heading: heading,
                value: filterValue,
            };
            
            handlechangea(filterData);
        }
    }, [handlechangea, listHeaders, data.DataType, data.title]);

    // Build chart options for Pie chart
    const chartOptions = useMemo(() => ({
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    right: 20,
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#1d252d',
                    bodyColor: '#1d252d',
                    borderColor: '#ffffff',
                    borderWidth: 0,
                    padding: 12,
                    cornerRadius: 8,
                    displayColors: true,
                    titleFont: {
                        size: 12,
                        weight: 'normal',
                    },
                    bodyFont: {
                        size: 13,
                        weight: 'bold',
                    },
                },
            },
            scales: {
                x: {
                    display: false,
                },
                y: {
                    display: false,
                },
            },
            elements: {
                arc: {
                    borderWidth: 0
                },
            },
        onHover: (event, elements) => {
            // Only show pointer cursor if handlechangea exists and clicking is enabled
            if (!handlechangea || data.DataType === 'NA') {
                return;
            }
            const canvas = event.native?.target;
            if (canvas) {
                canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
            }
        },
        onClick: (event, elements) => {
            // Disable clicking for widgets that should not add filters
            const noFilterTitles = ['Active/Inactive Brokers by Transactions', 'Active/Inactive Brokers by Enrollments'];
            if (data.DataType === 'NA' || noFilterTitles.some(t => data.title?.includes(t))) {
                return;
            }
            
            if (elements.length > 0 && handlechangea && pieChartData) {
                const index = elements[0].index;
                const label = pieChartData.labels[index];
                
                // Use DataType from data config, not listHeaders
                const heading = data.DataType || (listHeaders.length > 0 ? listHeaders[0] : data.title);
                
                // Only create filter if we have a valid heading
                if (!heading || heading === 'undefined') {
                    return;
                }
                
                let filterValue = label;
                
                // Strip state code from pharmacy city names
                // Handles formats: "CITY, ST" or "CITY ST"
                if (heading === 'PhCity') {
                    filterValue = String(filterValue)
                        .trim()
                        .replace(/,\s*[A-Z]{2}$/, '')  // Remove ", ST" format
                        .replace(/\s+[A-Z]{2}$/, '')   // Remove " ST" format
                        .trim();
                }
                
                const filterData = {
                    heading: heading,
                    value: filterValue,
                };
                
                try {
                    handlechangea(filterData);
                } catch (error) {
                    console.error('Error in handlechangea:', error);
                }
            }
        },
    }), [handlechangea, pieChartData, listHeaders, data]);

    // Fetch data
    const fetchData = useCallback(async () => {
        // Cancel any previous pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (!apidata || !apidata[0]) {
                setNoDataMessage('No data available');
                setIsLoading(false);
                return;
            }

            // Process legend data
            try {
                if (apidata[0].legend?.length > 0) {
                    setLegendValue(apidata[0].legend[0].legendValue);
                    setLegendText(apidata[0].legend[0].legendText);
                } else {
                    setLegendValue('0');
                    setLegendText('');
                }
            } catch {
                setLegendValue('0');
                setLegendText('');
            }

            // Process pie chart data
            try {
                const pieDataSet = apidata[0].pieDataSet?.[0];
                
                if (pieDataSet) {
                    const hasData = pieDataSet.datasets?.[0]?.data?.length > 1;
                    
                    if (!hasData) {
                        setNoDataMessage('No Data Available');
                    } else {
                        setNoDataMessage('');
                    }

                    // Transform colors for theme
                    const bgColors = transformColors(pieDataSet.datasets[0].backgroundColor);
                    const hoverColors = transformColors(pieDataSet.datasets[0].hoverBackgroundColor);

                    setPieChartData({
                        labels: pieDataSet.labels,
                        datasets: [{
                            data: pieDataSet.datasets[0].data,
                            backgroundColor: bgColors,
                            hoverBackgroundColor: hoverColors,
                            borderWidth: 0,
                        }],
                    });
                }
            } catch (err) {

            }

            // Process list data
            try {
                const listDataSet = apidata[0].listDataSet;
                if (listDataSet && listDataSet.length > 0) {
                    // First row is headers
                    const headers = listDataSet[0]?.datasets?.map(d => d.data) || [];
                    setListHeaders(headers);

                    // Rest is data
                    const rows = listDataSet.slice(1).map(row => 
                        row.datasets?.map(d => d.data) || []
                    );
                    setListData(rows);
                }
            } catch (err) {

            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') {
                return; // Expected cancel
            }

            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data.url, data.RequestJson, data.RequestJson1, handleResponse, transformColors]);

    // Fetch data when RequestJson changes (with null guard)
    useEffect(() => {
        if (data.type === 'Column_Pie' && data.RequestJson && data.url && 
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Initial fetch (with null guard and proper deps)
    useEffect(() => {
        if (data.type === 'Column_Pie' && data.RequestJson && data.url) {
            fetchData();
        }
        
        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Render chart content
    const chartContent = useMemo(() => {
        if (!pieChartData || !pieChartData.labels?.length) {
            return null;
        }

        return (
            <div className="h-full w-full flex items-center justify-center">
                <div className="w-full max-w-md h-full">
                    <Pie 
                        data={pieChartData} 
                        options={convertChartOptions(chartOptions)} 
                    />
                </div>
            </div>
        );
    }, [pieChartData, chartOptions]);

    // Additional controls (info tooltip for specific widgets)
    const additionalControls = useMemo(() => {
        if (userdata.userrole === 'Admin' && data.title === 'Transactions by Wholesaler') {
            return (
                <InfoHint message="Only accounts for group numbers assigned to Wholesalers" />
            );
        }
        return null;
    }, [userdata.userrole, data.title]);

    // Determine default view - show list by default for "Transactions by Drug" in admin
    const defaultView = useMemo(() => {
        if (data.title === 'Transactions by Drug' && userdata.userrole === 'Admin') {
            return 'list';
        }
        return 'chart';
    }, [data.title, userdata.userrole]);

    // Hide number in title only for "Paid Claims (%) by Pharmacy Chain"; show it for "Paid Claims by Pharmacy Chain"
    const showLegendInTitle = !hideLegend && data.title !== 'Paid Claims (%) by Pharmacy Chain';

    return (
        <ChartWidget
            title={data.title}
            subtitle={data.Subtitle}
            legendValue={showLegendInTitle ? legendValue : null}
            legendLabel=""
            isLoading={isLoading}
            error={error}
            noDataMessage={noDataMessage || 'No data available'}
            chartContent={chartContent}
            chartType="Pie"
            listData={listData}
            listHeaders={listHeaders}
            onListItemClick={!handlechangea || data.DataType === 'NA' || ['Active/Inactive Brokers by Transactions', 'Active/Inactive Brokers by Enrollments'].some(t => data.title?.includes(t)) ? undefined : handleListItemClick}
            onExport={handleExport}
            exportTitle={data.title}
            additionalControls={additionalControls}
            showToggle={true}
            defaultView={defaultView}
            minHeight="300px"
        />
    );
}

export default ColumnPieChart;

